import { ErrorHandler, Injectable } from '@angular/core';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { addAngularError } from '@datadog/browser-rum-angular';
import { secureRoutesFor } from '@presencelearning/auth';
import { getLogger, type LoggingConfig } from '@presencelearning/logging';
import { releaseVersion } from '../environments/release';

const SERVICE = 'future-room';
const SITE = 'datadoghq.com';

/** Origins that receive the Sirius bearer token and RUM trace headers. */
export function apiOrigins(apps: Record<string, { url: string }>, doorUrl = ''): string[] {
  return secureRoutesFor(apps, [doorUrl]);
}

export function matchesApiOrigin(url: string, origins: readonly string[]): boolean {
  try {
    return origins.includes(new URL(url).origin);
  } catch {
    return false;
  }
}

export function loggingConfig(env: string, clientToken: string): LoggingConfig | null {
  if (!clientToken) {
    return null;
  }
  return {
    service: SERVICE,
    env,
    version: releaseVersion,
    clientToken,
    site: SITE,
  };
}

export function rumInitConfiguration(
  applicationId: string,
  clientToken: string,
  env: string,
  origins: readonly string[]
): RumInitConfiguration | null {
  if (!applicationId || !clientToken) {
    return null;
  }
  return {
    applicationId,
    clientToken,
    site: SITE,
    service: SERVICE,
    env,
    version: releaseVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    traceContextInjection: 'sampled',
    allowedTracingUrls: [
      {
        match: (url: string) => matchesApiOrigin(url, origins),
        propagatorTypes: ['datadog', 'tracecontext'],
      },
    ],
  };
}

/** Reports an unhandled error to RUM and to the logging package. */
@Injectable()
export class FutureRoomErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    addAngularError(error);
    getLogger('angular').error(error, 'unhandled error');
    super.handleError(error);
  }
}
